import { Reducer, useEffect, useReducer } from "react"
import { useAuth } from "./auth"
import { FeedFilter, FeedItem, FeedType } from "../feed/data-types"
import { feed } from "../feed/feed"
import { fetchReadingLanguages, updateReadingLanguages } from "../feed/reading-languages"


type FeedState = {
    pending: boolean
    final: boolean
    feed?: Array<FeedItem>
    error?: string
    query: string
}

type UpdateFunction = (offset: number, limit: number, types: FeedType[], filter: FeedFilter | null, token?: string, force?: boolean) => Promise<void>

export const useFeed = (userLanguages: string[]): [FeedState, UpdateFunction] => {
    const [state, setState] = useReducer<Reducer<FeedState, Partial<FeedState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            final: true,
            feed: [],
            query: "",
        }
    )

    const update = async (offset: number, limit: number, types: FeedType[], filter: FeedFilter | null, token?: string, force?: boolean) => {
        const startState: Partial<FeedState> = { pending: true, final: false }
        if (force) {
            offset = 0
            startState.feed = []
        }

        setState(startState)
        try {
            const { data, error } = await feed(offset, limit, types, filter, userLanguages, token)
            if (error != null) {
                setState({ error: error, pending: false })
                return
            }

            if (!data?.length) {
                setState({ final: true, pending: false })
                return
            }
            
            if (!force) {
                setState({ feed: [ ...(state.feed || []), ...data], pending: false })
            } else {
                setState({ feed: data, pending: false })
            }
        } catch (e) {
            setState({ error: e as string, pending: false })
        }
    }

    return [state, update]
}

type ReadingLanguagesState = {
    data: string[]
    pending: boolean
}

export const useReadingLanguages = (): [ReadingLanguagesState, (languages: string[]) => void]=> {
    const { token } = useAuth()
    
    const [state, setState] = useReducer<Reducer<ReadingLanguagesState, Partial<ReadingLanguagesState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: true, 
            data: [],
        }
    )

    useEffect(() => {
        if (!token?.access) {
            return
        }

        (async () => {
            const languages = await fetchReadingLanguages(token.access)
            setState({
                pending: false,
                data: languages.map(l => l.toLowerCase()),
            })
        })()
    }, [token?.access])

    if (!token?.access) {
        return [{ pending: true, data: []}, () => {}]
    }

    const updateReadingLanguagesFn = async (languages: string[]) => {
        await updateReadingLanguages(languages, token.access)
        setState({
            data: languages
        })
    }

    return [state, updateReadingLanguagesFn]
}